import {
  CSSProperties,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import useAuth from "../../../../hooks/useAuth";
import { ModalShell } from "../../../atoms/ModalShell";
import { FaChevronDown, FaLock } from "react-icons/fa6";
import {
  RoleTypeEditorType,
  UserRole,
  UserType,
} from "../../../../types/UserRolesTypes";
import { CustomInput } from "../../../atoms/CustomInput";
import { CustomButton } from "../../../atoms/CustomButton";
import { CharacterCounter } from "../../../atoms/CharacterCounter";
import {
  useGetRoleList,
  useGetTypeList,
  useUpdateRole,
  useUpdateType,
} from "../../../../hooks/users";
import { useRolePermissions } from "../../../../utils/rolePermissions";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { AlertMessage } from "../../../atoms/AlertMessage";
import NoTranslate from "../../../atoms/NoTranslate";
import { FieldLabel } from "../../../atoms/FieldLabel";

const RoleTypeSelect = ({
  value,
  options,
  protectedValues,
  lockTitle,
  disabled,
  addLabel,
  addValue,
  onSelect,
}: {
  value: string;
  options: Array<{ value: string }>;
  protectedValues: string[];
  lockTitle: string;
  disabled: boolean;
  addLabel: string;
  addValue: string;
  onSelect: (value: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const positionMenu = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        setOpen(false);
        return;
      }
      const gap = 4;
      const estimatedHeight = 260;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
      setMenuStyle({
        position: "fixed",
        left: rect.left,
        minWidth: rect.width,
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + gap }
          : { top: rect.bottom + gap }),
      });
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    positionMenu();
    window.addEventListener("scroll", positionMenu, true);
    window.addEventListener("resize", positionMenu);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", positionMenu, true);
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const select = (v: string) => {
    setOpen(false);
    onSelect(v);
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`w-full min-w-0 pl-4 pr-3 py-2 bg-white rounded-md shadow border border-gray-300 justify-between items-center flex ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        <div className="text-gray-700 text-sm font-medium font-figtree leading-tight flex items-center gap-1.5 min-w-0">
          {protectedValues.includes(value) && (
            <FaLock
              size={14}
              className="text-amber-500 shrink-0"
              title={lockTitle}
            />
          )}
          <span className="truncate">{value}</span>
        </div>
        <div className="w-5 h-5 relative shrink-0">
          <FaChevronDown size={14} />
        </div>
      </button>

      {open &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[9998]"
              onClick={() => setOpen(false)}
            />
            <div
              role="menu"
              translate="no"
              style={menuStyle}
              className="z-[9999] w-fit rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none"
            >
              <div className="max-h-[200px] overflow-y-auto">
                {options.map((opt, index) => (
                  <button
                    type="button"
                    role="menuitem"
                    key={index}
                    onClick={() => select(opt.value)}
                    className="flex w-full items-center gap-1 whitespace-nowrap px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
                  >
                    {protectedValues.includes(opt.value) && (
                      <FaLock
                        size={14}
                        className="text-amber-500 shrink-0"
                        title={lockTitle}
                      />
                    )}
                    <span>
                      <NoTranslate>{opt.value}</NoTranslate>
                    </span>
                  </button>
                ))}
              </div>
              <div className="my-1 h-px bg-gray-100" />
              <button
                type="button"
                role="menuitem"
                onClick={() => select(addValue)}
                className="flex w-full items-center whitespace-nowrap px-4 py-2 text-left text-blue-500 hover:bg-gray-100"
              >
                {addLabel}
              </button>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

const schemaRole = Joi.object({
  role: Joi.string().trim().min(2).max(20).required().label("Role name"),
});

const shcemaType = Joi.object({
  type: Joi.string().trim().min(2).max(20).required().label("Type name"),
});

export const RoleModal = ({
  roles,
  showLabel = true,
  setRole,
  setRoles,
  setShowRoleModal,
  user_identification,
  entity_identification,
  customerId,
}: {
  roles: Array<UserRole>;
  showLabel?: boolean;
  setRole: Dispatch<SetStateAction<string>>;
  setRoles: Dispatch<SetStateAction<Array<UserRole>>>;
  setShowRoleModal: Dispatch<SetStateAction<boolean>>;
  user_identification: string;
  entity_identification: string;
  customerId: string;
}) => {
  const [t] = useTranslation();

  const { mutate: updateRole, isLoading } = useUpdateRole();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(schemaRole),
  });

  const onSubmit = (data: any) => {
    updateRole(
      {
        user_identification: user_identification,
        entity_identification: entity_identification,
        customerId: customerId,
        role: data.role,
      },
      {
        onSuccess: () => {
          setRoles([...roles, data]);
          setShowRoleModal(false);
          setRole(data.role.toUpperCase());
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CustomInput
        name="role"
        control={control}
        error={errors.role}
        helperText={errors.role?.message}
        label={
          showLabel
            ? (t("dashboard.RoleTypeEditor.modal.title-01") as string)
            : ""
        }
        maxLength={20}
      />
      <CharacterCounter
        currentLength={watch("role")?.length || 0}
        maxLength={20}
      />

      <div className="flex justify-end">
        <CustomButton
          variant="primary"
          className="mt-3"
          id="role"
          type="submit"
          isLoading={isLoading}
          disabled={isLoading}
          title={t("common.add")}
        />
      </div>
    </form>
  );
};

export const TypeModal = ({
  types,
  showLabel = true,
  setType,
  setTypes,
  setShowTypeModal,
  disabled,
  user_identification,
  entity_identification,
  customerId,
}: {
  types: Array<UserType>;
  showLabel?: boolean;
  setType: Dispatch<SetStateAction<string>>;
  setTypes: Dispatch<SetStateAction<Array<UserType>>>;
  setShowTypeModal: Dispatch<SetStateAction<boolean>>;
  disabled: boolean;
  user_identification: string;
  entity_identification: string;
  customerId: string;
}) => {
  const [t] = useTranslation();
  const { mutate: updateType, isLoading } = useUpdateType();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(shcemaType),
  });

  const onSubmit = (data: any) => {
    updateType(
      {
        user_identification: user_identification,
        entity_identification: entity_identification,
        customerId: customerId,
        type: data.type,
      },
      {
        onSuccess: () => {
          setTypes([...types, data.type]);
          setShowTypeModal(false);
          setType(data.type.toUpperCase());
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {showLabel && (
        <FieldLabel
          className="text-sm font-medium text-gray-700 block mb-3"
          value={t("dashboard.RoleTypeEditor.modal.title-02").toString()}
        />
      )}
      <CustomInput
        name="type"
        control={control}
        error={errors.type}
        helperText={errors.type?.message}
        label={t("dashboard.RoleTypeEditor.modal.placeholder-02") as string}
        maxLength={20}
      />
      <CharacterCounter
        currentLength={watch("type")?.length || 0}
        maxLength={20}
      />

      <div className="flex justify-end">
        <CustomButton
          variant="primary"
          className="mt-3"
          id="typeGroup"
          type="submit"
          disabled={disabled}
          isLoading={isLoading}
          title={t("common.add")}
        />
      </div>
    </form>
  );
};

const RoleTypeEditor = ({
  data,
  customerId,
  entity_identification,
  user_identification,
}: RoleTypeEditorType) => {
  const [t] = useTranslation();

  const { user, userEndedSubscription } = useAuth();
  const rolePermissions = useRolePermissions(user);
  const permissions = usePermissions(user);

  const canViewRole = permissions.can(Cap.USERS_USER_ROLE, Level.VIEW);
  const canViewType = permissions.can(Cap.USERS_USER_TYPE, Level.VIEW);

  const isSelf = user.user_identification === customerId;
  const canModifyRole =
    permissions.can(Cap.USERS_USER_ROLE, Level.WRITE) &&
    (isSelf ? rolePermissions.canModifyUser(customerId, "role") : true);
  const canModifyType =
    permissions.can(Cap.USERS_USER_TYPE, Level.WRITE) &&
    (isSelf ? rolePermissions.canModifyUser(customerId, "type") : true);

  const [role, setRole] = useState<string>(data.role);
  const [type, setType] = useState<string>(data.type);

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);

  const [roles, setRoles] = useState<Array<UserRole>>([]);
  const [types, setTypes] = useState<Array<UserType>>([]);

  const protectedRoles = ["OWNER", "LOYALTY_PROG_USER"];

  const protectedTypes = ["ADMIN"];

  const prevCustomerIdRef = useRef<string>(customerId);

  useEffect(() => {
    if (prevCustomerIdRef.current !== customerId) {
      setRole(data.role);
      setType(data.type);
      prevCustomerIdRef.current = customerId;
    }
  }, [data, customerId]);

  const {
    data: dataRole,
    isSuccess: isSuccessRoles,
    isLoading: isLodingRole,
    isError: isErrorRole,
    refetch: roleRefetch,
  } = useGetRoleList({
    entity_identification: entity_identification,
    user_identification: user_identification,
    enabled: canViewRole,
  });

  useEffect(() => {
    setRoles(dataRole || []);
  }, [dataRole]);

  const {
    data: dataType,
    isSuccess: isSuccessTypes,
    isLoading: isLoadingType,
    isError: isErrorType,
    refetch: typeRefetch,
  } = useGetTypeList({
    entity_identification: entity_identification,
    user_identification: user_identification,
    enabled: canViewType,
  });

  useEffect(() => {
    setTypes(dataType || []);
  }, [dataType]);

  const { mutate: updateRole } = useUpdateRole();
  const { mutate: updateType } = useUpdateType();

  const handleRoleManagement = (e: string) => {
    const optionValue = e;

    if (optionValue !== "addRole") {
      updateRole(
        {
          user_identification: user_identification,
          entity_identification: entity_identification,
          customerId: customerId,
          role: optionValue,
        },
        {
          onSuccess: () => setRole(optionValue),
        }
      );
    } else {
      setShowRoleModal(true);
    }
  };

  const handleTypeManagement = (e: string) => {
    const optionValue = e;

    if (optionValue !== "addType") {
      updateType(
        {
          user_identification: user_identification,
          entity_identification: entity_identification,
          customerId: customerId,
          type: optionValue,
        },
        { onSuccess: () => setType(optionValue) }
      );
    } else {
      setShowTypeModal(true);
    }
  };

  let roleContent;

  if (canViewRole && (isLodingRole || isLoadingType))
    roleContent = <div>loading...</div>;

  if (canViewRole && isErrorRole)
    roleContent = <CustomButton onClick={() => roleRefetch()} title="Reload" />;

  if (canViewRole && isSuccessRoles)
    roleContent = (
      <div className="flex items-center justify-between gap-4" translate="no">
        <div className="text-sm font-medium text-gray-700 shrink-0">
          {t("dashboard.entities.viewEntityUser.key7")}
        </div>
        <div className="min-w-0 flex-1">
          <RoleTypeSelect
            value={role}
            options={(roles || []).map((r: UserRole) => ({
              value: r.user_role,
            }))}
            protectedValues={protectedRoles}
            lockTitle="Protected role"
            disabled={!canModifyRole}
            addLabel={t("dashboard.RoleTypeEditor.btn-02")}
            addValue="addRole"
            onSelect={handleRoleManagement}
          />
        </div>
      </div>
    );

  let typeContent;

  if (canViewType && isErrorType)
    typeContent = <CustomButton onClick={() => typeRefetch()} title="Reload" />;

  if (canViewType && isSuccessTypes)
    typeContent = (
      <div className="flex items-center justify-between gap-4" translate="no">
        <div className="text-sm font-medium text-gray-700 shrink-0">
          {t("dashboard.entities.viewEntityUser.key8")}
        </div>
        <div className="min-w-0 flex-1">
          <RoleTypeSelect
            value={type}
            options={(types || []).map((tp: UserType) => ({
              value: tp.user_type,
            }))}
            protectedValues={protectedTypes}
            lockTitle="Protected type"
            disabled={!canModifyType}
            addLabel={t("dashboard.RoleTypeEditor.btn-03")}
            addValue="addType"
            onSelect={handleTypeManagement}
          />
        </div>
      </div>
    );

  return (
    <div className="w-full">
      {isSelf && !canModifyRole && (
        <AlertMessage color="warning" className="mb-4">
          <span className="font-medium">Restricted Access</span>
          <p className="mt-1">You cannot modify your own role or type.</p>
        </AlertMessage>
      )}

      <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        {roleContent}

        {typeContent}
      </div>

      <ModalShell
        size="lg"
        show={showRoleModal}
        onClose={() => setShowRoleModal(false)}
        title={t("dashboard.RoleTypeEditor.modal.header-01")}
        subtitle={t("dashboard.RoleTypeEditor.modal.description-01")}
      >
        <RoleModal
          roles={roles}
          setRoles={setRoles}
          setShowRoleModal={setShowRoleModal}
          setRole={setRole}
          customerId={customerId}
          entity_identification={entity_identification}
          user_identification={user_identification}
        />
      </ModalShell>

      <ModalShell
        size="lg"
        show={showTypeModal}
        onClose={() => setShowTypeModal(false)}
        title={t("dashboard.RoleTypeEditor.modal.header-02")}
        subtitle={t("dashboard.RoleTypeEditor.modal.description-02")}
      >
        <TypeModal
          types={types}
          setTypes={setTypes}
          setShowTypeModal={setShowTypeModal}
          setType={setType}
          disabled={userEndedSubscription}
          customerId={customerId}
          entity_identification={entity_identification}
          user_identification={user_identification}
        />
      </ModalShell>
    </div>
  );
};

export default RoleTypeEditor;
